import { ConfigProvider, Form } from 'antd';
import { Input, TextArea, Button, Select } from "mobily-ui-library/lib";
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEmailRecipient, notifyEmail } from '../../../api/api';
import { GlobalContext } from '../../../context/GlobalContext';
import { Error } from '../../Util/Util';

import './NotifyForm.scss';

interface Props {
    setSuccess: (value: boolean) => void;
    rtl: boolean;
    packages: any;
    center: any;
}

function NotifyForm({ setSuccess, rtl, packages, center }: Props) {
    const [fullName, setFullName] = useState('');
    const [idNumber, setIDNumber] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [landNumber, setLandNumber] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [simPackage, setSimPackage] = useState('');
    const [emailRecipient, setEmailRecipient] = useState('');
    const { dispatch }: any = useContext(GlobalContext);

    const [form] = Form.useForm();
    const { t } = useTranslation();
    const direction = rtl ? 'rtl' : 'ltr';

    useEffect(() => {
        let cancel = true;

        getEmailRecipient()
            .then((res: any) => {
                if (!cancel) return;
                if (res.data.status.statusCode === "MBE_000") {
                    dispatch({ type: 'SET_LOADER', payload: false });
                    setEmailRecipient(res.data.data.project[0].recipients_list)
                }
            }).catch(err => {
                Error(err.message);
                dispatch({ type: 'SET_LOADER', payload: false })
            })
        return () => {
            cancel = false;
        }
    }, [dispatch]);

    const packagesType = packages.map((pack: any) => {
        return {
            icon: '',
            text: rtl ? pack.packageNameAr : pack.packageNameEn,
            id: pack.packageId
        }
    });

    const submitForm = () => {
        const coordinates = `${center.lat}, ${center.lng}`;
        const payload = {
            templateId: "500004",
            emailFrom: "info@mobily.com.sa",
            emailTo: emailRecipient,
            emailCC: "",
            subjectParamValues: [
                { "value": "966540540543" }
            ],
            bodyParamValues: [
                { "value": fullName },
                { "value": idNumber },
                { "value": phoneNumber },
                { "value": null },
                { "value": email },
                { "value": address },
                { "value": simPackage },
                { "value": coordinates }
            ]
        }

        dispatch({ type: 'SET_LOADER', payload: true });
        notifyEmail(payload)
            .then((res: any) => {
                if (res.status === 204) {
                    setTimeout(() => {
                        setSuccess(true);
                        dispatch({ type: 'SET_LOADER', payload: false });
                    }, 1000)
                } else {
                    const err = rtl ? res.status.arabicDetail : res.status.englishDetail;
                    console.log(err);
                    dispatch({ type: 'SET_LOADER', payload: false });
                };
            }).catch(err => {
                console.log(err.message)
                dispatch({ type: 'SET_LOADER', payload: false });
            });
    }

    const handleSimPackage = (value: any) => {
        setSimPackage(value);
    }

    return (
        <div className='Notify__Form'>
            <ConfigProvider direction={direction}>
                <Form form={form} name="control-ref" onFinish={submitForm}>
                    <div className='formField__flexRow'>
                        <Form.Item
                            initialValue={fullName}
                            hasFeedback
                            name='fullName'
                            className={rtl ? 'form__fieldItem arabic-text' : 'form__fieldItem'}
                            rules={[{ required: true, message: t('FullName_validation') }]}
                        >
                            <Input
                                rtl={rtl}
                                placeholder={t('Your_fullName')}
                                headingleft={t('Full_name')}
                                className='receiverName_inputField'
                                width={'410px'}
                                onChange={(e: any) => setFullName(e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            initialValue={idNumber}
                            hasFeedback
                            name='idNumber'
                            className={rtl ? 'form__fieldItem arabic-text' : 'form__fieldItem'}
                            rules={[
                                { required: true, message: t('IDNumber_validation') },
                            ]}
                        >
                            <Input
                                rtl={rtl}
                                placeholder="XX XX XX XX XX"
                                headingleft={t('ID_number')}
                                headingright={t('ID_Type')}
                                labelleft=""
                                onChange={(e: any) => setIDNumber(e.target.value)}
                            />
                        </Form.Item>
                    </div>

                    <div className='formField__flexRow'>
                        <Form.Item
                            initialValue={phoneNumber}
                            hasFeedback
                            name='phoneNumber'
                            className={rtl ? 'form__fieldItem phoneNumber_inputField arabic-text' : 'form__fieldItem phoneNumber_inputField'}
                            rules={[
                                { required: true, message: t('MobileNumber_missing') },
                                {
                                    message: t('MobileNumber_validation'),
                                    pattern: /^(((9665)([0-9]{8}))||((05)(5|0|3|6|4|9|1|8|7|2)([0-9]{7})))$/
                                }
                            ]}
                        >
                            <Input
                                rtl={rtl}
                                placeholder='05XXXXXXXX'
                                headingleft={t('Mobile_number')}
                                headingright={t('MSISDN')}
                                maxLength={phoneNumber.startsWith('9665') ? 12 : 10}
                                onChange={(e: any) => setPhoneNumber(e.target.value)}
                            />
                        </Form.Item>

                        <Form.Item
                            hasFeedback
                            initialValue={landNumber}
                            name='landNumber'
                            className={rtl ? 'form__fieldItem arabic-text' : 'form__fieldItem'}
                            rules={[{ required: false, message: t('LandNumber_validation') }]}
                        >
                            <Input
                                rtl={rtl}
                                placeholder={t('Land_number')}
                                headingleft={t('Land_number')}
                                headingright={t('Optional')}
                                className='deliveryNotes_TextField'
                                onChange={(e: any) => setLandNumber(e.target.value)}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        hasFeedback
                        initialValue={email}
                        name='email'
                        className={rtl ? 'form__fieldItem email_inputField arabic-text' : 'form__fieldItem email_inputField'}
                        rules={[
                            { required: true, message: t('Email_missing') },
                            {
                                message: t('Email_validation'),
                                pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z.-]+\.[a-zA-Z]{2,6}$/
                            }
                        ]}
                    >
                        <Input
                            rtl={rtl}
                            placeholder="email@address.com"
                            headingleft={t("Email")}
                            onChange={(e: any) => setEmail(e.target.value)}
                        />
                    </Form.Item>

                    <div className='horizontalLine' />

                    <div className='formField__flexRow'>

                        <Form.Item
                            hasFeedback
                            initialValue={address}
                            name='detailAddress'
                            className={rtl ? 'formField__detailAddress arabic-text' : 'formField__detailAddress'}
                            rules={[{ required: true, message: t('Address_validation') }]}
                        >
                            <TextArea
                                placeholder={t('Type_here')}
                                headingleft={t('Detailed_address')}
                                labelleft=''
                                onChange={(e: any) => setAddress(e.target.value)}
                                value={address}
                            />
                        </Form.Item>

                        <Form.Item
                            hasFeedback
                            name='desiredPackage'
                            className={rtl ? 'form__fieldItem map--fieldItem arabic-text' : 'form__fieldItem map--fieldItem'}
                            rules={[{ required: true, message: t('Package_validation') }]}
                        >
                            <Select
                                headingLeft={t('Desired_package')}
                                placeholder={t('Select_package')}
                                rtl={rtl}
                                options={packagesType}
                                onChange={handleSimPackage}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item>
                        <div className='submitButton'>
                            <Button
                                primary={true}
                                rtl={rtl}
                                // variant={(reciverEmail === '' || reciverPhone === '' || reciverAddress === '') || !deliveryZone ? 'disabled' : 'default'}
                                htmlType={'submit'}
                            >
                                {t('Submit')}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </ConfigProvider>
        </div>
    )
}

export default NotifyForm